import { useMemo } from 'react';
import DataTable from '@/components/DataTable.jsx';
import { useSeasonData } from './useSeasonData.js';
import './FixtureTickerTab.css';

/*
 * Players against their team's upcoming fixtures.
 *
 * Distinct from the /ticker page, which is teams down the side and every
 * gameweek across: this one keeps the player list — sortable by points,
 * filterable by name or position — and puts each player's next fixtures
 * beside them, so two players can be compared on form and run at once.
 *
 * `fixtures` is only present on the current season's payload; past seasons
 * omit it, and the tab is hidden rather than shown empty.
 */
export function seasonHasFixtures(data) {
	return Boolean(data?.history?.[0]?.fixtures && Object.keys(data.history[0].fixtures).length);
}

export default function FixtureTickerTab({ season }) {
	const { data, status, error } = useSeasonData(season);

	const { columns, rows } = useMemo(() => {
		const source = data?.history ?? [];
		const slots = Object.keys(source[0]?.fixtures ?? {});

		/* Flatten each fixture into its own field so it can carry a column. */
		const flat = source.map((player) => {
			const out = {
				id: player.id,
				web_name: player.web_name,
				team_name: player.team_name,
				position: player.position,
				total_points: player.total_points,
			};
			slots.forEach((slot) => {
				out[`fx${slot}`] = player.fixtures?.[slot] ?? null;
			});
			return out;
		});

		const cols = [
			{ key: 'web_name', label: 'Player', title: 'Player name', strong: true },
			{ key: 'team_name', label: 'Team' },
			{ key: 'position', label: 'Pos', title: 'Position', tone: (v) => v },
			{
				key: 'total_points',
				label: 'Pts',
				title: 'Total points',
				numeric: true,
				defaultSort: 'desc',
			},
			...slots.map((slot) => {
				/* Label with the real gameweek rather than a slot number. */
				const gw = source[0]?.fixtures?.[slot]?.gw;
				return {
					key: `fx${slot}`,
					label: gw ? `GW${gw}` : `+${Number(slot) + 1}`,
					title: gw ? `Gameweek ${gw}` : `Fixture ${Number(slot) + 1}`,
					/* Every cell in the column holds an object, so sorting and the
					   text filter have nothing meaningful to work with. */
					sortable: false,
					tone: (fixture) => (fixture?.diff ? `diff-${fixture.diff}` : undefined),
					render: (fixture) => fixture?.opp ?? '—',
				};
			}),
		];

		return { columns: cols, rows: flat };
	}, [data]);

	if (status === 'loading') {
		return (
			<div className="tab-loading" role="status">
				<span className="spinner" aria-hidden="true" />
				<span>Loading fixtures…</span>
			</div>
		);
	}
	if (status === 'error') {
		return (
			<div className="notice notice--error" role="alert">
				<strong>Could not load fixtures.</strong>
				<span>{error}</span>
			</div>
		);
	}

	return (
		<div className="fixture-ticker-tab">
			<DataTable
				columns={columns}
				rows={rows}
				rowKey={(r) => r.id}
				filterPlaceholder="Filter by player, team or position"
				maxHeight="70vh"
			/>
			<p className="ticker-legend">
				<span className="ticker-swatch" data-diff="1" /> Easiest
				<span className="ticker-swatch" data-diff="2" />
				<span className="ticker-swatch" data-diff="3" />
				<span className="ticker-swatch" data-diff="4" />
				<span className="ticker-swatch" data-diff="5" /> Hardest
			</p>
		</div>
	);
}
